import { inject, Injectable } from '@angular/core';
import { of, switchMap } from 'rxjs';
import { SharedUserApi } from '../api/user/shared-user.api';
import { SharedViewerApi } from '../api/viewer/shared-viewer.api';

import { type User } from '@reconcept-platform/shared/types';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SharedViewerService {
  private sharedUserApi = inject(SharedUserApi);
  private sharedViewerApi = inject(SharedViewerApi);

  getViewer() {
    return this.sharedViewerApi.watchViewer().pipe(take(1));
  }

  watchViewerWithAvatar() {
    return this.sharedViewerApi.watchViewerWithAvatar();
  }

  watchViewerOidc() {
    return this.sharedViewerApi.watchViewerOidc();
  }

  destroyViewerOidc(id: string) {
    return this.sharedViewerApi.destroyOidc(id);
  }

  // store a viewer custom setting in the user's account
  setViewerCustomSetting(key: string, value: any) {
    return this.sharedUserApi.watchViewerCustomSettings().pipe(
      take(1),
      switchMap((viewer) => {
        if (!viewer) {
          return of(null);
        }

        // TODO: check whether this can actually ever be null, otherwise remove
        // hacky ternary. It was added to ensure backwards compatibility while
        // refactoring types
        const customSettings = (viewer.customSettings as any) ? { ...viewer.customSettings } : {};
        // set key value
        customSettings[key] = value;

        // update backend
        return this.sharedUserApi.updateUser({
          userId: viewer.id,
          customSettings,
        });
      }),
    );
  }

  // store a ui preference
  storeUiPreference(preferenceKey: string, value: string | boolean | number) {
    return this.getViewerCustomSetting('ui-preferences-web').pipe(
      switchMap((preferences) => {
        // store the custom settings in user
        return this.setViewerCustomSetting('ui-preferences-web', {
          ...(preferences ?? {}),
          [preferenceKey]: value,
        });
      }),
    );
  }

  // get a ui preference
  getUiPreference(preferenceKey: string) {
    // the ui preference can either be stored in account settings or in localstorage
    return this.watchUiPreference(preferenceKey).pipe(take(1));
  }

  // get a ui preference
  watchUiPreference(preferenceKey: string) {
    return this.watchViewerCustomSettings().pipe(
      map((viewer) => this.getSetting(viewer, 'ui-preferences-web.' + preferenceKey)),
    );
  }

  // get a viewer custom setting
  getViewerCustomSetting(key: string) {
    return this.watchViewerCustomSettings().pipe(
      map((viewer) => this.getSetting(viewer, key)),
      take(1),
    );
  }

  watchViewerCustomSettings() {
    return this.sharedUserApi.watchViewerCustomSettings();
  }

  private getSetting(viewer: Pick<User, 'id' | 'customSettings'>, key: string) {
    if (!viewer || !viewer.customSettings) {
      return undefined;
    }

    // try to return the requested value
    return key.split('.').reduce((prev, curr) => {
      return prev ? prev[curr] : undefined;
    }, viewer.customSettings);
  }
}
